import { Editor } from '@tiptap/core';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { MMM_DD_YYYY } from 'utility/constants';
import { EditorialConfigData, FullEditorialAsset } from 'utility/types/dataProvider';
import { findLinks, formatDate, isFiniteNumber } from '../../../../utility/functions';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(timezone);
dayjs.extend(utc);

// Function to update the placeholder dynamically
// See SeoSettings.tsx for usage example
export function updateTiptapPlaceholder(
  editor: Editor | null,
  newPlaceholder: string,
) {
  if (!editor || editor.isDestroyed) {
    return;
  }

  const placeholderExtension = editor?.extensionManager?.extensions?.find(
    (ext) => ext.name === 'placeholder',
  );
  if (placeholderExtension) {
    placeholderExtension.options.placeholder = newPlaceholder;
    editor.view.dispatch(editor.state.tr.setMeta('placeholder', newPlaceholder));
  }
}

/**
 * Returns the formatted expiration date for a given data object.
 * If both archiveTime and deleteTime exist, the earliest date is returned.
 * If neither exist, the string "Never" is returned.
 *
 * @param data - The data object containing potential expiration times.
 * @param siteTz - Optional timezone for formatting.
 * @returns {string} The formatted expiration date or "Never".
 */
export const getExpiry = (data: Partial<FullEditorialAsset>, siteTz?: string): string => {
  // Cannot destructuring assignment here for create mode
  const archive_time = data?.archive_time;
  const delete_time = data?.delete_time;

  // If neither archive_time nor delete_time exist, return 'Never'
  if (!archive_time && !delete_time) {
    return 'Never';
  }

  const archiveTime = archive_time ? dayjs.utc(archive_time) : null;
  const deleteTime = delete_time ? dayjs.utc(delete_time) : null;

  if (archiveTime && deleteTime) {
    // Both dates exist; return the earliest one
    if (archiveTime.isBefore(deleteTime)) {
      return (
        formatDate({ date: archive_time, format: MMM_DD_YYYY, shiftTz: siteTz }) ||
        'Invalid Date'
      );
    } else {
      return (
        formatDate({ date: delete_time, format: MMM_DD_YYYY, shiftTz: siteTz }) ||
        'Invalid Date'
      );
    }
  }

  if (archiveTime) {
    return (
      formatDate({ date: archive_time, format: MMM_DD_YYYY, shiftTz: siteTz }) ||
      'Invalid Date'
    );
  }

  if (deleteTime) {
    return (
      formatDate({ date: delete_time, format: MMM_DD_YYYY, shiftTz: siteTz }) ||
      'Invalid Date'
    );
  }

  return 'Invalid Date';
};

/**
 * Extracts UUIDs from a given set based on a specified type.
 *
 * @param set - Array of items.
 * @param type - Desired type to filter by - item?.type
 * @returns {string[]} - Array of UUIDs.
 */
export const getIdsFromItems = (items?: any[], type?: string): string[] => {
  const uuids: string[] = [];

  if (!Array.isArray(items)) return uuids;

  for (const item of items) {
    if (item?.type === type) {
      // Blox assets will have a UUID, syndication assets will have an ID
      const id = item?.uuid || item?.id;
      if (id) {
        uuids.push(id);
      }
    }
  }

  return uuids;
};

/**
 * Calculates the estimated print depth from total characters and a factor.
 *
 * @param totalCharacters - The total number of characters.
 * @param printDepthFactor - The divisor for the character count.
 * @returns {number} - Estimated print depth, rounded to one decimal place.
 */
export const calculateEstimatedPrintDepth = (
  totalCharacters: number,
  printDepthFactor: number,
): number => {
  const estimatedPrintDepth = totalCharacters / (printDepthFactor || 1);
  return Math.round(estimatedPrintDepth * 10) / 10;
};

/**
 * Calculates the estimated print depth by counting all text characters
 * in the TipTap JSON document and dividing by the provided printDepthFactor.
 *
 * @param docNode - The root JSONContent node from editorInstance.getJSON().
 * @param printDepthFactor - The divisor for total character count to calculate print depth.
 * @returns {number} - Estimated print depth, rounded to one decimal place.
 */
export type ItemCount = {
  total: number; // total count deduped childs + parent + sibling + inline
  uuids: string[]; // ids total deduped childs + parent + sibling + inline
  inlineUuids: string[]; // inlined ids via editor
  childUuids: string[]; // child ids via record
  parentUuids: string[]; // parent ids via record
  siblingUuids: string[]; // sibling ids via record
};

/**
 * Counts specific items within the editors and record.
 *
 * @param editorInstance - tiptap editor instance.
 * @param type - Type of the item to count ('image', 'video', etc.).
 * @param activeRecord - The current active record data.
 * @param includeChildren - Whether to include children item in the count.
 * @param includeParents - Whether to include parents item in the count.
 * @param includeSiblings - Whether to include siblings item in the count.
 * @returns EditorItemCount object:
 */

export const countItemsByType = (
  editorInstance: Editor,
  type: string,
  activeRecord?: FullEditorialAsset,
): ItemCount => {
  // get all <blox-inline> nodes from the editors content
  const jsonContent = editorInstance?.getJSON();
  const bloxInlineItems =
    jsonContent?.content?.filter((row: any) => row?.type === 'bloxInline') || [];

  // get all uuids from the asset record data - deduplicate using sets
  const childIds = getIdsFromItems(activeRecord?.children, type);
  const childIdSet = new Set(childIds);
  const parentIds = getIdsFromItems(activeRecord?.parents, type);
  const parentIdSet = new Set(parentIds);
  const siblingIds = getIdsFromItems(activeRecord?.siblings, type);
  const siblingIdSet = new Set(siblingIds);
  const allIds = new Set<string>([...childIdSet, ...parentIdSet, ...siblingIdSet]);

  // tracking inlined items
  const inlineIds = new Set<string>([]);

  // check inline items for uuid and type match
  for (const inline of bloxInlineItems) {
    const typeAttr = inline?.attrs?.asset_type;
    const uuidAttr = inline?.attrs?.uuid;

    // skip if no uuid or type mismatch
    if (!uuidAttr || !typeAttr || typeAttr !== type) {
      continue;
    }

    inlineIds.add(uuidAttr); // track inline usage
    allIds.add(uuidAttr); // also add inline id to full count
  }

  const result = {
    total: allIds.size,
    uuids: Array.from(allIds),
    inlineUuids: Array.from(inlineIds),
    childUuids: Array.from(childIdSet),
    parentUuids: Array.from(parentIdSet),
    siblingUuids: Array.from(siblingIdSet),
  };
  return result;
};

/**
 * Get all UUIDs within the editor's content.
 *
 * @param editorInstance - The TipTap editor instance.
 * @returns {string[]} - Array of all UUIDs found in the editor content.
 */
export const getInlineAssetUUIDs = (editorInstance: Editor): string[] => {
  const htmlContent = editorInstance.getHTML();
  const bloxInlineMatches = htmlContent.match(/<blox-inline[^>]*>/g) || [];
  const uuidSet = new Set<string>();

  bloxInlineMatches.forEach((inline: string) => {
    const uuidAttribute = inline.match(/\buuid="([^"]+)"/)?.[1];
    if (uuidAttribute) {
      uuidSet.add(uuidAttribute);
    }
  });

  return Array.from(uuidSet);
};

export type CalculateEditorStateParams = {
  editor: Editor | null; // Basic TipTap editor instance
  // Extra params used for more complex stats on 'content' editor
  isContentEditor?: boolean;
  editorialConfig?: EditorialConfigData;
  recordData?: FullEditorialAsset; // formData / record obj
};

export type EditorStateCalculation = {
  text?: string;
  html?: string;
  wordCount?: number;
  printWordCount?: number;
  characterCount?: number;
  printCharacterCount?: number;
  imageInfo?: ItemCount;
  videoInfo?: ItemCount;
  audioInfo?: ItemCount;
  linkCount?: number;
  inlineAssetUUIDs?: string[];
  depth?: number;
};

/**
 * Calculates some states of the editor, including content, word count, character count,
 * image count, video count, audio count, link count, inlineAssetUUIDs, depth, etc.
 */

export function calculateEditorState({
  editor,
  isContentEditor = false,
  recordData,
  editorialConfig,
}: CalculateEditorStateParams) {
  if (!editor) {
    return undefined;
  }

  // Validate print depth factor
  const depthFactor = Number(editorialConfig?.asset_config?.article?.printDepthFactor);
  const isValidDepthFactor = isFiniteNumber(depthFactor) && depthFactor > 0;

  // Use tiptap utilities to get text, word count, and character count
  const textContent = editor.getText();
  const htmlContent = editor.getHTML();
  const wordCount = editor?.storage?.characterCount?.words();
  const characterCount = editor?.storage?.characterCount?.characters();

  // For Content Editor, prepare filtered metrics that exclude words inside elements with class "note" and characters inside elements with class "print_trim" for depth calculation.
  let filteredWordCount: number | undefined; // word count excluding notes
  let depthCharCount: number | undefined; // character count excluding print_trim
  let printWordCount: number | undefined; // word count excluding print_trim
  let printCharacterCount: number | undefined; // character count excluding print_trim
  if (isContentEditor) {
    const filtered = getFilteredContentMetrics(htmlContent);
    filteredWordCount = filtered.wordCountExcludingNotes;
    depthCharCount = filtered.characterCountExcludingPrintTrim;
    printWordCount = filtered.wordCountExcludingPrintTrim;
    printCharacterCount = filtered.characterCountExcludingPrintTrim;
  }

  let result: EditorStateCalculation = {
    text: textContent,
    html: htmlContent,
    // If filteredWordCount was computed for content editor use it, else fallback
    wordCount: filteredWordCount ?? wordCount,
    printWordCount: printWordCount,
    characterCount: characterCount,
    printCharacterCount: printCharacterCount,
  };

  // Only do these counts if 'content' editor
  if (isContentEditor) {
    // Calculate the document's depth - CMSADMIN-1063
    // Excludes text within elements classed "note" or "print_trim" using filtered HTML metrics.
    if (isValidDepthFactor && depthCharCount !== undefined) {
      const depth = calculateEstimatedPrintDepth(depthCharCount, depthFactor);
      result.depth = depth;
    }

    // Count images
    const imageResults = countItemsByType(editor, 'image', recordData);
    result.imageInfo = imageResults;

    // Count videos
    const videoResults = countItemsByType(editor, 'video', recordData);
    result.videoInfo = videoResults;

    // Count audio
    const audioResults = countItemsByType(editor, 'audio', recordData);
    result.audioInfo = audioResults;

    // Count links
    const linkCount = findLinks(editor.getHTML()).length;
    result.linkCount = linkCount;

    // Get all UUIDs
    const inlineAssetUuids = getInlineAssetUUIDs(editor);
    result.inlineAssetUUIDs = inlineAssetUuids;
  }
  return result;
}

/**
 * Derives filtered metrics from editor HTML.
 * - wordCountExcludingNotes: excludes text inside elements with class 'note'
 * - characterCountExcludingPrintTrim: excludes characters inside elements with class 'note' or 'print_trim'
 * @param html - The HTML content from the editor.
 * @returns An object containing the filtered word and character counts.
 */
export function getFilteredContentMetrics(html: string): {
  wordCountExcludingNotes: number;
  characterCountExcludingPrintTrim: number;
  wordCountExcludingPrintTrim: number;
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let words: string[] = [];
  let printWords: string[] = [];
  let depthChars = 0;

  function traverse(node: Node, inNote: boolean, inPrintTrim: boolean) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const nextInNote = inNote || el.classList.contains('note');
      const nextInPrintTrim = inPrintTrim || el.classList.contains('print_trim');
      for (const child of Array.from(el.childNodes)) {
        traverse(child, nextInNote, nextInPrintTrim);
      }
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!inNote) {
        // Stndard word count excluding notes
        words.push(...tokenizeWords(text));
      }
      if (!inNote && !inPrintTrim) {
        // Print Word count excluding print_trim and notes
        printWords.push(...tokenizeWords(text));
        depthChars += text.length;
      }
    }
  }

  traverse(doc.body, false, false);
  // Filter empty tokens
  words = words.filter(Boolean);
  printWords = printWords.filter(Boolean);
  return {
    wordCountExcludingNotes: words.length,
    characterCountExcludingPrintTrim: depthChars,
    wordCountExcludingPrintTrim: printWords.length,
  };
}

// Basic word tokenizer approximating tiptap's characterCount.words logic.
function tokenizeWords(text: string): string[] {
  // Replace punctuation/separators with spaces, keep apostrophes/hyphens inside words
  const cleaned = text.replace(/[^\w'\-\u00C0-\u017F]+/g, ' ').trim();
  if (!cleaned) return [];
  return cleaned.split(/\s+/);
}
